import { Box, createStyles, Divider, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { TranquiTheme } from '../../containers/App/theme';
import { OutDebt } from '../../generated/graphql';
import { CssSpreadType } from '../../utils/css-spread-type';
import { getDebtConfigByType } from '../../utils/getDebtConfigByType';
import { getNegBooleans } from '../../utils/getNegBooleans';
import getVirtualDebtChildrenBalance from '../../utils/getVirtualDebtChildrenBalance';
import thousandSeparator from '../../utils/thousandSeparator';
import { getJcapInterests } from '../../utils/getJcapInterests';

interface ACPKDebtsSummaryTableProps {
  debt: OutDebt;
  className?: string;
  classNameTextTable?: string;
  dataTest?: string;
  withBackground?: boolean;
  title?: string;
}

const ACPKDebtsSummaryTable: React.FC<ACPKDebtsSummaryTableProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const {
    debt, className, classNameTextTable,
    dataTest, withBackground, title,
  } = props;
  const { isVirtualDebt, isJcap, isTranqui, isBogota } = getNegBooleans(debt);
  const getBalance = isJcap
    ? (od: OutDebt) => ((od.balance || 0) + getJcapInterests(od)) || 0
    : ({ balance }: OutDebt) => balance || 0;
  const balanceACPK = getVirtualDebtChildrenBalance(debt?.extra?.children || [], getBalance);

  if (!isVirtualDebt) return null;

  const showBalances = !(isTranqui && isBogota);

  return (
    <Box className={cn(className)}>
      {title && (
      <Typography className={cn(classes.textTable, classes.fullLine, 'mb-1')} align="left">
        {title}
      </Typography>
      )}
      <Box
        className={cn(showBalances && classes.productsTable, withBackground && classes.background)}
        data-test={dataTest}
      >
        <Typography className={classes.titleTable}>Producto</Typography>
        {showBalances && (<Typography className={classes.titleTable} align="right">Saldo</Typography>)}
        {
        (debt.extra?.children || []).map((
          child,
        ) => (
          <React.Fragment key={child.id}>
            <Typography className={cn(classes.textTable, classNameTextTable)}>
              {getDebtConfigByType(child.type)?.label}
              {' '}
              {child.productNumber}
            </Typography>
            {
              showBalances && (
                <Typography className={cn(classes.textTable, classNameTextTable)} align="right">
                  $ {thousandSeparator(getBalance(child))}
                </Typography>
              )
            }
          </React.Fragment>
        ))
      }
        {showBalances && (
        <>
          <Divider className={cn(classes.divider, 'my-1')} />
          <Typography
            className={cn(
              classes.balanceTextTable,
              withBackground && classes.acpkTableText,
              classNameTextTable,
            )}
          >
            <strong>Saldo total</strong>
          </Typography>
          <Typography className={cn(classes.textTable, withBackground && classes.acpkTableText, classNameTextTable)} align="right">
            $ {thousandSeparator(balanceACPK)}
          </Typography>
        </>
        )}

      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  productsTable: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    width: '100%',
  },
  background: {
    border: `solid 2px ${theme.palette.lavender.main}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    backgroundColor: `${theme.palette.lavender.main}05`,
  },
  acpkTableText: {
    color: theme.palette.text3.main,
    fontSize: '0.8rem',
    fontWeight: 400,
  },
  titleTable: {
    ...(theme.typography.capitalize as CssSpreadType),
    color: theme.palette.text4.main,
    textTransform: 'uppercase',
  },
  textTable: {
    ...(theme.typography.small as CssSpreadType),
    color: theme.palette.text4.main,
  },
  fullLine: {
    gridColumn: '1 / 3',
  },
  balanceTextTable: {
    ...theme.typography.body2,
    color: theme.palette.text4.main,
  },
  divider: {
    gridColumn: '1 / 3',
    width: '100%',
  },
}));

export default ACPKDebtsSummaryTable;
